/**
 * DecisionPause.jsx — Predict stage of the Predict→Observe→Explain loop.
 *
 * STAGE 1 (REASON): student must pick the correct physical reasoning.
 *   Each distractor is tagged with a misconception; wrong picks are logged.
 *   The equation is HIDDEN until reasoning is correct — understanding
 *   unlocks the formula, not the other way around.
 * STAGE 2 (VALUE): numeric prediction using the now-revealed equation.
 *   Wrong value → consequence preview (Observe), then ConsequenceModal
 *   requires an explanation (Explain) before retry.
 */
import { useState, useEffect, useMemo } from "react";
import { useGame, WORLDS } from "./store";
import PhysicsDiagram from "./PhysicsDiagram";
import { getReasoningQuestion, getFormulaQuestion } from "./questionBank";
import { playChime, playBuzzer } from "./sound";

const fmt = (n, d = 1) => (n === undefined || isNaN(n) ? "—" : Number(n).toFixed(d));

// ─── Scenario Panel (collision / impulse / nozzle POE) ────────────
// Mirrors DecisionPause2 styling but lives inside DecisionPause for Phase 1.
const KIND_COLORS_SC = {
  incline:  "#ffd23f",
  banking:  "#00e5ff",
  drag:     "#a855f7",
  // legacy (kept so old saves don't crash)
  collision: "#ef4444",
  impulse:   "#ffd23f",
  nozzle:    "#a855f7",
};

function ScenarioPanel({ decision }) {
  const sc = decision.scenario;
  const accent = KIND_COLORS_SC[decision.kind] ?? "#00e5ff";

  const [stage,        setStage]        = useState("reason");
  const [wrongReason,  setWrongReason]  = useState([]);
  const [reasonFB,     setReasonFB]     = useState(null);
  const [wrongFormula, setWrongFormula] = useState([]);
  const [formulaFB,    setFormulaFB]    = useState(null);
  const [numInput,     setNumInput]     = useState("");
  const [valueFB,      setValueFB]      = useState(null);
  const [attempts,     setAttempts]     = useState(0);

  // Reset when question changes
  useEffect(() => {
    setStage("reason"); setWrongReason([]); setReasonFB(null);
    setWrongFormula([]); setFormulaFB(null); setNumInput(""); setValueFB(null); setAttempts(0);
  }, [decision?.scenarioId]);

  const pickReason = (opt) => {
    if (wrongReason.includes(opt.id) || reasonFB?.ok) return;
    if (opt.correct) {
      playChime(); setReasonFB({ ok: true, msg: "✓ Correct! Now pick the right equation." });
      setTimeout(() => setStage("formula"), 1400);
    } else {
      playBuzzer(); setWrongReason((p) => [...p, opt.id]);
      setReasonFB({ ok: false, header: "✗ NOT QUITE", msg: opt.whyWrong });
    }
  };

  const pickFormula = (opt) => {
    if (wrongFormula.includes(opt.id) || formulaFB?.ok) return;
    if (opt.correct) {
      playChime(); setFormulaFB({ ok: true, msg: "✓ Correct equation! Now calculate." });
      setTimeout(() => setStage("value"), 1400);
    } else {
      playBuzzer(); setWrongFormula((p) => [...p, opt.id]);
      setFormulaFB({ ok: false, header: "✗ WRONG EQUATION", msg: opt.whyWrong });
    }
  };

  const submitValue = () => {
    const val = parseFloat(numInput);
    if (isNaN(val)) { setValueFB({ ok: false, header: "Enter a number", msg: "Type a numeric answer." }); return; }
    const { correct, tolerance } = sc.value;
    if (Math.abs(val - correct) <= tolerance) {
      playChime();
      setValueFB({ ok: true, msg: `✓ Correct! Answer = ${correct} ${sc.value.unit ?? ""}` });
      setTimeout(() => useGame.setState({ paused: false, decision: null }), 2200);
    } else {
      playBuzzer(); setAttempts((a) => a + 1);
      const msg = val > correct ? sc.value.whyWrongHigh : sc.value.whyWrongLow;
      setValueFB({ ok: false, header: "✗ NOT QUITE", msg });
    }
  };

  const revealAnswer = () => {
    setValueFB({ ok: false, header: "📖 WORKED ANSWER", msg: sc.value.hint, reveal: true });
    setTimeout(() => useGame.setState({ paused: false, decision: null }), 4000);
  };

  const Opt = ({ opt, picked, done, onClick }) => {
    const bad = picked.includes(opt.id);
    const good = done && opt.correct;
    return (
      <button onClick={() => onClick(opt)} disabled={bad || done} style={{
        textAlign: "left", padding: "11px 15px", borderRadius: 10,
        fontSize: 13.5, lineHeight: 1.55, fontFamily: "inherit", cursor: bad || done ? "default" : "pointer",
        background: good ? "rgba(0,255,170,0.15)" : bad ? "rgba(255,77,109,0.07)" : "rgba(255,255,255,0.05)",
        border: good ? "1px solid rgba(0,255,170,0.6)" : bad ? "1px solid rgba(255,77,109,0.35)" : "1px solid rgba(255,255,255,0.15)",
        color: good ? "#00ffaa" : bad ? "#6a5560" : "#e8ecf3",
        textDecoration: bad ? "line-through" : "none", opacity: bad ? 0.6 : 1,
      }}>{opt.text}</button>
    );
  };

  const FB = ({ fb }) => !fb ? null : (
    <div style={{ marginTop: 12, borderRadius: 10, overflow: "hidden",
      background: fb.ok ? "rgba(0,255,170,0.1)" : "rgba(255,77,109,0.08)",
      border: `1px solid ${fb.ok ? "rgba(0,255,170,0.4)" : "rgba(255,77,109,0.35)"}` }}>
      {fb.header && <div style={{ padding: "7px 13px", fontSize: 11, fontWeight: 800, letterSpacing: 2,
        background: fb.ok ? "rgba(0,255,170,0.15)" : "rgba(255,77,109,0.18)",
        color: fb.ok ? "#00ffaa" : "#ff8a9b" }}>{fb.header}</div>}
      <div style={{ padding: "10px 13px", fontSize: 13, lineHeight: 1.65, color: fb.ok ? "#a0ffe8" : "#ffd0d8" }}>{fb.msg}</div>
    </div>
  );

  const STAGE_LABELS = { reason: "STEP 1 / 3 — REASON", formula: "STEP 2 / 3 — EQUATION", value: "STEP 3 / 3 — CALCULATE" };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(2,4,12,0.88)", backdropFilter: "blur(12px)", zIndex: 25,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ width: "min(740px,100%)", maxHeight: "calc(100vh - 40px)", overflowY: "auto",
        background: "linear-gradient(155deg,#12152e,#08090f)", border: `2px solid ${accent}55`,
        borderRadius: 22, padding: "26px 28px", color: "#fff", boxShadow: `0 20px 80px ${accent}22` }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: accent, fontWeight: 700, marginBottom: 4 }}>
              ⏸ SCENARIO EVENT — {decision.kind?.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: "#5a6478", letterSpacing: 1 }}>{STAGE_LABELS[stage]}</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["reason","formula","value"].map((st) => (
              <div key={st} style={{ width: 10, height: 10, borderRadius: 5,
                background: stage === st ? accent : (["reason","formula","value"].indexOf(stage) > ["reason","formula","value"].indexOf(st) ? "#00ffaa66" : "#2a2d40"),
                border: `1px solid ${stage === st ? accent : "#3a3d50"}` }} />
            ))}
          </div>
        </div>

        {/* Scenario */}
        <div style={{ background: `linear-gradient(135deg,${accent}16,transparent)`, border: `1px solid ${accent}30`,
          borderRadius: 12, padding: "13px 16px", marginBottom: 18, fontSize: 15, lineHeight: 1.65, fontWeight: 500 }}>
          {sc.setup}
        </div>

        {stage === "reason" && (
          <div>
            <p style={{ color: "#cfd6e3", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{sc.reason.question}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sc.reason.options.map((opt) => <Opt key={opt.id} opt={opt} picked={wrongReason} done={!!reasonFB?.ok} onClick={pickReason} />)}
            </div>
            <FB fb={reasonFB} />
          </div>
        )}
        {stage === "formula" && (
          <div>
            <p style={{ color: "#cfd6e3", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{sc.formula.question}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sc.formula.options.map((opt) => <Opt key={opt.id} opt={opt} picked={wrongFormula} done={!!formulaFB?.ok} onClick={pickFormula} />)}
            </div>
            <FB fb={formulaFB} />
          </div>
        )}
        {stage === "value" && (
          <div>
            <p style={{ color: "#cfd6e3", fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{sc.value.question}</p>
            {attempts >= 2 && (
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,200,100,0.25)",
                borderRadius: 8, padding: "9px 13px", fontSize: 12.5, fontFamily: "monospace", color: "#9aa4b2", marginBottom: 12 }}>
                💡 Hint: {sc.value.hint}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              <input type="number" step="any" value={numInput}
                onChange={(e) => setNumInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !valueFB?.ok && submitValue()}
                placeholder={`Answer in ${sc.value.unit ?? "units"}`}
                style={{ flex: 1, padding: "12px 15px", background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10, color: "#fff", fontSize: 16, fontFamily: "inherit", outline: "none" }} />
              <button onClick={submitValue} disabled={!!valueFB?.ok}
                style={{ padding: "12px 20px", borderRadius: 10, border: "none",
                  background: valueFB?.ok ? "#00ffaa33" : `linear-gradient(135deg,${accent},${accent}cc)`,
                  color: valueFB?.ok ? "#00ffaa" : "#0a0c1e", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                {valueFB?.ok ? "✓" : "Check →"}
              </button>
            </div>
            <FB fb={valueFB} />
            {attempts >= 3 && !valueFB?.ok && !valueFB?.reveal && (
              <button onClick={revealAnswer} style={{ width: "100%", marginTop: 8, padding: "10px",
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 8, color: "#9aa4b2", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                📖 Show worked answer and continue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DecisionPause() {
  const paused = useGame((s) => s.paused);
  const decision = useGame((s) => s.decision);
  const world = useGame((s) => s.world);
  const upcomingRadius = useGame((s) => s.upcomingRadius);
  const upcomingSlope = useGame((s) => s.upcomingSlope);
  const mass = useGame((s) => s.mass);
  const retryPrefill = useGame((s) => s.retryPrefill);

  // Stage: "reason" → "formula" → "value"
  const [stage, setStage] = useState("reason");
  const [reasonQ, setReasonQ] = useState(null);
  const [pickedWrong, setPickedWrong] = useState([]); // option ids picked wrongly
  const [reasonFeedback, setReasonFeedback] = useState(null);
  const [formulaQ, setFormulaQ] = useState(null);
  const [formulaWrong, setFormulaWrong] = useState([]);
  const [formulaFeedback, setFormulaFeedback] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!decision) return;
    // Retry after the Explain step: reasoning was already done — go straight
    // to the numeric stage with the wrong value prefilled for comparison.
    const isRetry = retryPrefill !== null;
    setStage(isRetry ? "value" : "reason");
    setReasonQ(isRetry ? null : getReasoningQuestion(decision.kind));
    setFormulaQ(isRetry ? null : getFormulaQuestion(decision.kind));
    setPickedWrong([]);
    setReasonFeedback(null);
    setFormulaWrong([]);
    setFormulaFeedback(null);
    setAnswer(isRetry ? String(retryPrefill) : "");
    setFeedback(null);
    if (isRetry) useGame.setState({ retryPrefill: null });
  }, [decision]);

  const W = WORLDS[world] || WORLDS.EARTH;
  const g = W.g;
  const r = Math.min(upcomingRadius, 9999);
  const slope = upcomingSlope;

  // Compute the CORRECT answer (kept hidden from the student)
  const correct = useMemo(() => {
    if (!decision) return 0;
    if (decision.kind === "curve" || decision.kind === "hilltop") {
      return Math.sqrt(g * r * Math.cos(slope));
    } else if (decision.kind === "jump") {
      return 18;
    } else if (decision.kind === "brake") {
      return (decision.v0 * decision.v0) / (2 * 12);
    } else if (decision.kind === "energycrest") {
      return Math.sqrt(2 * g * (decision.dh ?? 5.2));
    } else if (decision.kind === "accel") {
      // v = √(u² + 2·a·s), u=0
      const a = decision.a ?? 4;
      const s = decision.s_meters ?? 200;
      return Math.sqrt(2 * a * s);
    } else if (decision.kind === "stopdist") {
      // d = u²/(2a)
      const u = decision.u ?? 30;
      const a = decision.a ?? 12;
      return (u * u) / (2 * a);
    } else if (decision.kind === "launchspeed") {
      // u = √(v_target² + 2·a_drag·s)
      const v = decision.v_target ?? 28;
      const a = decision.a ?? 1.5;
      const s = decision.s_meters ?? 300;
      return Math.sqrt(v * v + 2 * a * s);
    }
    return decision.correctV ?? 0;
  }, [decision, g, r, slope]);

  if (!paused || !decision) return null;

  // ─── Delegate scenario questions to ScenarioPanel ─────────────
  if (decision.kind === "scenario") {
    return <ScenarioPanel decision={decision} />;
  }

  // ─── STAGE 1: reasoning choice ───────────────────────────────
  const pickReason = (opt) => {
    if (pickedWrong.includes(opt.id) || stage !== "reason") return;
    if (opt.correct) {
      playChime();
      const firstTry = pickedWrong.length === 0;
      useGame.getState().logPrediction(decision.kind, firstTry);
      setReasonFeedback({
        ok: true,
        msg: "Correct reasoning! Next: which EQUATION expresses it?",
      });
      setTimeout(() => setStage("formula"), 1400);
    } else {
      playBuzzer();
      useGame.getState().logMisconception(opt.tag);
      setPickedWrong((p) => [...p, opt.id]);
      // Show WHY this specific option is wrong — stays visible until they pick correctly
      setReasonFeedback({ ok: false, msg: opt.whyWrong, tag: opt.tag });
    }
  };

  // ─── STAGE 2: formula choice ─────────────────────────────────
  const pickFormula = (opt) => {
    if (formulaWrong.includes(opt.id) || stage !== "formula") return;
    if (opt.correct) {
      playChime();
      setFormulaFeedback({
        ok: true,
        msg: "Right equation, right reason. Now plug in the numbers.",
      });
      setTimeout(() => setStage("value"), 1400);
    } else {
      playBuzzer();
      useGame.getState().logMisconception(opt.tag);
      setFormulaWrong((p) => [...p, opt.id]);
      // Show WHY this specific formula is wrong — stays visible until they pick correctly
      setFormulaFeedback({ ok: false, msg: opt.whyWrong, tag: opt.tag });
    }
  };

  // ─── STAGE 2: numeric prediction ─────────────────────────────
  const submit = () => {
    const v = parseFloat(answer);
    if (isNaN(v)) {
      setFeedback({ ok: false, msg: "Please enter a numeric value." });
      return;
    }
    let ok = false;
    if (decision.kind === "curve" || decision.kind === "hilltop") {
      const tol = correct * 0.15;
      ok = Math.abs(v - correct) <= tol;
    } else if (decision.kind === "jump") {
      ok = v >= correct * 0.95 && v <= 30;
    } else if (decision.kind === "brake" || decision.kind === "energycrest") {
      ok = Math.abs(v - correct) <= correct * 0.15;
    } else if (decision.kind === "accel" || decision.kind === "stopdist" || decision.kind === "launchspeed") {
      ok = Math.abs(v - correct) <= correct * 0.12;
    }

    // ── PHASE 2 outcomes ──
    if (decision.kind === "brake") {
      if (ok) {
        useGame.setState({ brakePlan: { d: v, correctD: correct, v0: decision.v0 } });
        setFeedback({ ok: true, msg: `Good plan! Your BRAKE marker is set ${v.toFixed(0)} m before the line. Coast, then brake (↓) at the marker!` });
        setTimeout(() => useGame.setState({ paused: false, decision: null, decisionAttempts: 0 }), 1800);
      } else if (v > correct) {
        setFeedback({ ok: false, msg: `Longer than physics needs — d = v²/(2a) = ${decision.v0?.toFixed(1)}²/24. Try a tighter plan.` });
      } else {
        setFeedback({ ok: false, msg: `That's a SHORT distance for ${decision.v0?.toFixed(1)} m/s… let's test your plan. Brace!` });
        setTimeout(() => {
          useGame.setState({
            paused: false, decision: null,
            brakePlan: { d: v, correctD: correct, v0: decision.v0 },
            consequencePreview: true,
            savedDecision: decision, savedSpeed: v, savedCorrectAnswer: correct,
            decisionAttempts: 0,
          });
        }, 1600);
      }
      return;
    }
    if (decision.kind === "energycrest") {
      if (ok) {
        useGame.setState({ energyPlan: { vMin: correct } });
        setFeedback({ ok: true, msg: `Correct! v_min ≈ ${correct.toFixed(1)} m/s. Enter the ENGINE-OFF climb at or just above it — KE pays for the height.` });
        setTimeout(() => useGame.setState({ paused: false, decision: null, decisionAttempts: 0 }), 1800);
      } else if (v > correct) {
        setFeedback({ ok: false, msg: `More than physics needs — energy balance: ½v² = gΔh → v = √(2·${g}·${(decision.dh ?? 5.2).toFixed(1)}).` });
      } else {
        setFeedback({ ok: false, msg: `Too slow to make it… let's test: watch the KE run out on the climb.` });
        setTimeout(() => {
          useGame.setState({
            paused: false, decision: null,
            energyPlan: { vMin: v },
            consequencePreview: true,
            savedDecision: decision, savedSpeed: v, savedCorrectAnswer: correct,
            decisionAttempts: 0,
          });
        }, 1600);
      }
      return;
    }

    // ── SUVAT corridor outcomes ──
    if (decision.kind === "accel") {
      const label = `v = √(2 × ${decision.a ?? 4} × ${decision.s_meters ?? 350}) = ${correct.toFixed(1)} m/s`;
      if (ok) {
        playChime();
        setFeedback({ ok: true, msg: `Correct! Final speed ≈ ${correct.toFixed(1)} m/s. Watch the v-t graph climb as the corridor accelerates you!` });
        useGame.setState({ p2Results: [...(useGame.getState().p2Results ?? []), { id: "CA", label: "Corridor A", outcome: "correct", points: 30 }] });
        setTimeout(() => useGame.setState({ paused: false, decision: null }), 1800);
      } else {
        playBuzzer();
        setFeedback({ ok: false, msg: `Not quite — ${label}. Check: did you take the square root of 2·a·s?` });
      }
      return;
    }
    if (decision.kind === "stopdist") {
      const label = `d = ${decision.u ?? 30}²/(2×${decision.a ?? 12}) = ${correct.toFixed(1)} m`;
      if (ok) {
        playChime();
        setFeedback({ ok: true, msg: `Correct! Stopping distance ≈ ${correct.toFixed(1)} m. Your brake flag is set — hit ↓ at the marker!` });
        useGame.setState({ brakePlan: { d: v, correctD: correct }, p2Results: [...(useGame.getState().p2Results ?? []), { id: "CB", label: "Corridor B", outcome: "correct", points: 30 }] });
        setTimeout(() => useGame.setState({ paused: false, decision: null }), 1800);
      } else if (v < correct) {
        playBuzzer();
        setFeedback({ ok: false, msg: `Too short — ${label}. Remember: d ∝ v², not v. Double speed = FOUR times the distance.` });
      } else {
        playBuzzer();
        setFeedback({ ok: false, msg: `A bit generous — ${label}. Try again.` });
      }
      return;
    }
    if (decision.kind === "launchspeed") {
      const label = `u = √(${decision.v_target ?? 28}² + 2×${decision.a ?? 1.5}×${decision.s_meters ?? 500}) = ${correct.toFixed(1)} m/s`;
      if (ok) {
        playChime();
        setFeedback({ ok: true, msg: `Correct! Launch at ≈ ${correct.toFixed(1)} m/s and you'll arrive at the loop entry perfectly.` });
        useGame.setState({ p2Results: [...(useGame.getState().p2Results ?? []), { id: "CC", label: "Corridor C", outcome: "correct", points: 30 }] });
        setTimeout(() => useGame.setState({ paused: false, decision: null }), 1800);
      } else if (v < correct) {
        playBuzzer();
        setFeedback({ ok: false, msg: `Too slow — ${label}. You need more initial speed to compensate for drag over ${decision.s_meters ?? 500} m.` });
      } else {
        playBuzzer();
        setFeedback({ ok: false, msg: `A bit fast — ${label}. You'd arrive over the target speed.` });
      }
      return;
    }

    if (ok) {
      // Unlock the HUD safe-speed target for this hazard
      if (decision.hazardId) {
        useGame.setState((s) => ({
          hazardVmax: { ...s.hazardVmax, [decision.hazardId]: correct },
        }));
      }
      useGame.getState().setTargetSpeed(parseFloat(v.toFixed(1)));
      setFeedback({
        ok: true,
        msg: decision.hazardId
          ? `Correct! v_max ≈ ${correct.toFixed(1)} m/s is now on your HUD. Now DRIVE it — stay under the limit!`
          : `Correct! Resuming at ${v.toFixed(1)} m/s.`,
      });
      setTimeout(() => {
        useGame.setState({
          paused: false,
          decision: null,
          decisionAttempts: 0,
          greenSinceMs: 0,
        });
      }, 1800);
    } else if (decision.hazardId && v < correct) {
      // Too LOW for a hazard: no crash to show — coach and let them retry
      setFeedback({
        ok: false,
        msg: `Too cautious — the physics allows more speed. Re-check: v_max = √(g·r·cosθ) with r = ${fmt(r, 1)} m.`,
      });
    } else {
      // Too HIGH — Observe stage: launch the cart at THEIR speed and let
      // real physics play out in slow motion (it will derail in the curve)
      setFeedback({
        ok: false,
        msg: `Let's test your prediction. Entering the curve at ${v.toFixed(1)} m/s…`,
      });
      setTimeout(() => {
        useGame.setState({
          paused: false,
          decision: null,
          consequencePreview: true,
          // slow-mo starts at the derailment itself, not on approach
          forceSpeed: v,
          savedDecision: decision,
          savedSpeed: v,
          savedCorrectAnswer: correct,
          decisionAttempts: 0,
        });
      }, 1600);
    }
  };

  // Build clue text — only shown in the VALUE stage
  let equation = "";
  let given = "";
  let parameterPrompt = "";
  let unit = "m/s";

  if (decision.kind === "curve" || decision.kind === "hilltop") {
    equation = "v_max = √( g · r · cos θ )";
    given = `g = ${g} m/s² · r = ${fmt(r, 1)} m · θ = ${fmt((slope * 180) / Math.PI, 1)}°`;
    parameterPrompt = "Calculate v_max (the maximum safe speed). Enter it below.";
  } else if (decision.kind === "brake") {
    equation = "v² = u² − 2·a·d  →  d = u² / (2a)";
    given = `your speed u = ${fmt(decision.v0, 1)} m/s · braking a = 12 m/s² · final v = 0`;
    parameterPrompt = "Calculate the minimum BRAKING DISTANCE d. Enter it below.";
    unit = "m";
  } else if (decision.kind === "energycrest") {
    equation = "½·m·v² = m·g·Δh  →  v_min = √( 2·g·Δh )";
    given = `g = ${g} m/s² · climb height Δh ≈ ${fmt(decision.dh ?? 5.2, 1)} m · engine OFF on the climb`;
    parameterPrompt = "Calculate the minimum ENTRY SPEED v_min. Enter it below.";
  } else if (decision.kind === "jump") {
    equation = "R = v₀² · sin(2θ) / g  →  for θ = 45°,  v₀ = √( R · g )";
    given = `g = ${g} m/s² · gap range R ≈ 32 m · take-off angle θ ≈ 45°`;
    parameterPrompt = "Calculate the minimum take-off speed v₀. Enter it below.";
  } else if (decision.kind === "accel") {
    equation = "v² = u² + 2·a·s  →  v = √( 2·a·s )  (u = 0)";
    given = `u = 0 (from rest) · a = ${fmt(decision.a ?? 4, 1)} m/s² · s = ${decision.s_meters ?? 200} m`;
    parameterPrompt = "Calculate the FINAL SPEED v at the end of the corridor. Enter it below.";
  } else if (decision.kind === "stopdist") {
    equation = "v² = u² − 2·a·d  →  d = u² / (2a)  (final v = 0)";
    given = `u = ${fmt(decision.u ?? 30, 1)} m/s · deceleration a = ${fmt(decision.a ?? 12, 1)} m/s² · final v = 0`;
    parameterPrompt = "Calculate the STOPPING DISTANCE d (in metres). Enter it below.";
    unit = "m";
  } else if (decision.kind === "launchspeed") {
    equation = "v² = u² − 2·a·s  →  u = √( v² + 2·a·s )";
    given = `target arrival v = ${fmt(decision.v_target ?? 28, 1)} m/s · drag a = ${fmt(decision.a ?? 1.5, 1)} m/s² · s = ${decision.s_meters ?? 300} m`;
    parameterPrompt = "Calculate the LAUNCH SPEED u needed. Enter it below.";
  }

  const isReason = stage === "reason";

  return (
    <div
      data-testid="decision-pause"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(2,4,12,0.85)",
        backdropFilter: "blur(10px)",
        zIndex: 25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          width: "min(720px, 100%)",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          background: "linear-gradient(160deg, #1a1d3a 0%, #0a0c1e 100%)",
          border: `2px solid ${(feedback?.ok || reasonFeedback?.ok) ? "#00ffaa" : "#ffd23f"}`,
          borderRadius: 22,
          padding: 30,
          color: "#fff",
          boxShadow: `0 24px 80px ${(feedback?.ok || reasonFeedback?.ok) ? "rgba(0,255,170,0.3)" : "rgba(255,210,63,0.18)"}`,
          transition: "all 0.3s",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 3,
              color: "#ffd23f",
              fontWeight: 700,
            }}
          >
            ⏸ DECISION POINT
          </div>
          {/* Stage indicator */}
          <div
            data-testid="poe-stage"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              padding: "4px 10px",
              borderRadius: 100,
              background: "rgba(62,182,255,0.15)",
              color: "#3eb6ff",
              fontWeight: 700,
              border: "1px solid rgba(62,182,255,0.5)",
            }}
          >
            {stage === "reason"
              ? "STEP 1 / 3 — REASON"
              : stage === "formula"
                ? "STEP 2 / 3 — EQUATION"
                : "STEP 3 / 3 — PREDICT"}
          </div>
        </div>
        <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.3 }}>
          {decision.label}
        </h2>

        {/* 2D Situation Diagram — equation hidden until it has been EARNED */}
        <PhysicsDiagram
          kind={decision.kind}
          g={g}
          r={r}
          slope={slope}
          mass={mass}
          v0={decision.v0}
          dh={decision.dh}
          showEquation={stage === "value"}
        />

        {/* ─── STAGE 1: REASONING ─── */}
        {isReason && reasonQ && (
          <div style={{ marginTop: 18 }} data-testid="reasoning-stage">
            <p style={{ color: "#cfd6e3", fontSize: 15, lineHeight: 1.6, fontWeight: 600 }}>
              {reasonQ.question}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {reasonQ.options.map((opt) => {
                const wrong = pickedWrong.includes(opt.id);
                const done = reasonFeedback?.ok;
                const isCorrectPick = done && opt.correct;
                return (
                  <button
                    key={opt.id}
                    data-testid={`reason-option-${opt.id}`}
                    onClick={() => pickReason(opt)}
                    disabled={wrong || done}
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      borderRadius: 12,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      fontFamily: "inherit",
                      cursor: wrong || done ? "default" : "pointer",
                      background: isCorrectPick
                        ? "rgba(0,255,170,0.15)"
                        : wrong
                          ? "rgba(255,77,109,0.08)"
                          : "rgba(255,255,255,0.05)",
                      border: isCorrectPick
                        ? "1px solid rgba(0,255,170,0.6)"
                        : wrong
                          ? "1px solid rgba(255,77,109,0.4)"
                          : "1px solid rgba(255,255,255,0.15)",
                      color: isCorrectPick ? "#00ffaa" : wrong ? "#7a6470" : "#e8ecf3",
                      textDecoration: wrong ? "line-through" : "none",
                      opacity: wrong ? 0.65 : 1,
                      transition: "all 0.2s",
                    }}
                  >
                    {opt.text}
                  </button>
                );
              })}
            </div>

            {reasonFeedback && (
              <div
                data-testid="reason-feedback"
                style={{
                  marginTop: 14,
                  borderRadius: 12,
                  background: reasonFeedback.ok
                    ? "rgba(0,255,170,0.14)"
                    : "rgba(255,77,109,0.10)",
                  border: `1px solid ${reasonFeedback.ok ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.45)"}`,
                  overflow: "hidden",
                }}
              >
                <div style={{
                  padding: "8px 14px",
                  background: reasonFeedback.ok ? "rgba(0,255,170,0.2)" : "rgba(255,77,109,0.2)",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  color: reasonFeedback.ok ? "#00ffaa" : "#ff8a9b",
                }}>
                  {reasonFeedback.ok ? "✓ CORRECT REASONING" : "✗ THAT'S A COMMON MISCONCEPTION"}
                </div>
                <div style={{
                  padding: "12px 14px",
                  color: reasonFeedback.ok ? "#a0ffe8" : "#ffd0d8",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                  fontWeight: 500,
                }}>
                  {reasonFeedback.msg}
                </div>
                {!reasonFeedback.ok && (
                  <div style={{ padding: "0 14px 12px", fontSize: 12, color: "#9aa4b2" }}>
                    The option above is crossed out. Pick a different answer — try again.
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 12, fontSize: 11, color: "#5a6478", textAlign: "center" }}>
              Pick the correct physics first — then choose the equation, then calculate.
            </div>
          </div>
        )}

        {/* ─── STAGE 2: FORMULA SELECTION ─── */}
        {stage === "formula" && formulaQ && (
          <div style={{ marginTop: 18 }} data-testid="formula-stage">
            <p style={{ color: "#cfd6e3", fontSize: 15, lineHeight: 1.6, fontWeight: 600 }}>
              {formulaQ.question}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {formulaQ.options.map((opt) => {
                const wrong = formulaWrong.includes(opt.id);
                const done = formulaFeedback?.ok;
                const isCorrectPick = done && opt.correct;
                return (
                  <button
                    key={opt.id}
                    data-testid={`formula-option-${opt.id}`}
                    onClick={() => pickFormula(opt)}
                    disabled={wrong || done}
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      borderRadius: 12,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      fontFamily: "'JetBrains Mono', monospace",
                      cursor: wrong || done ? "default" : "pointer",
                      background: isCorrectPick
                        ? "rgba(0,255,170,0.15)"
                        : wrong
                          ? "rgba(255,77,109,0.08)"
                          : "rgba(255,255,255,0.05)",
                      border: isCorrectPick
                        ? "1px solid rgba(0,255,170,0.6)"
                        : wrong
                          ? "1px solid rgba(255,77,109,0.4)"
                          : "1px solid rgba(255,255,255,0.15)",
                      color: isCorrectPick ? "#00ffaa" : wrong ? "#7a6470" : "#e8ecf3",
                      textDecoration: wrong ? "line-through" : "none",
                      opacity: wrong ? 0.65 : 1,
                      transition: "all 0.2s",
                    }}
                  >
                    {opt.text}
                  </button>
                );
              })}
            </div>

            {formulaFeedback && (
              <div
                data-testid="formula-feedback"
                style={{
                  marginTop: 14,
                  borderRadius: 12,
                  background: formulaFeedback.ok
                    ? "rgba(0,255,170,0.14)"
                    : "rgba(255,77,109,0.10)",
                  border: `1px solid ${formulaFeedback.ok ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.45)"}`,
                  overflow: "hidden",
                }}
              >
                <div style={{
                  padding: "8px 14px",
                  background: formulaFeedback.ok ? "rgba(0,255,170,0.2)" : "rgba(255,77,109,0.2)",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  color: formulaFeedback.ok ? "#00ffaa" : "#ff8a9b",
                }}>
                  {formulaFeedback.ok ? "✓ CORRECT EQUATION" : "✗ WRONG EQUATION — HERE'S WHY"}
                </div>
                <div style={{
                  padding: "12px 14px",
                  color: formulaFeedback.ok ? "#a0ffe8" : "#ffd0d8",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                  fontWeight: 500,
                }}>
                  {formulaFeedback.msg}
                </div>
                {!formulaFeedback.ok && (
                  <div style={{ padding: "0 14px 12px", fontSize: 12, color: "#9aa4b2" }}>
                    That equation is crossed out. Think about which formula contains the variables you actually have.
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 12, fontSize: 11, color: "#5a6478", textAlign: "center" }}>
              Choose the equation AND the reason it applies — wrong picks explain themselves.
            </div>
          </div>
        )}

        {/* ─── STAGE 3: NUMERIC PREDICTION ─── */}
        {stage === "value" && (
          <>
            <p
              style={{
                color: "#cfd6e3",
                fontSize: 14,
                lineHeight: 1.6,
                marginTop: 14,
              }}
            >
              {parameterPrompt}
            </p>

            <div
              style={{
                marginTop: 8,
                padding: "14px 16px",
                background: "rgba(255,255,255,0.04)",
                borderRadius: 10,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              <div style={{ fontSize: 11, color: "#9aa4b2", marginBottom: 4 }}>
                YOUR EQUATION (EARNED ✓)
              </div>
              <div style={{ color: "#ffd23f", fontSize: 17 }}>{equation}</div>
              <div style={{ fontSize: 11, color: "#9aa4b2", marginTop: 12 }}>
                GIVEN
              </div>
              <div style={{ color: "#3eb6ff", fontSize: 14 }}>{given}</div>
            </div>

            <div style={{ marginTop: 20 }}>
              <label
                style={{
                  fontSize: 12,
                  color: "#9aa4b2",
                  letterSpacing: 1,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                YOUR PREDICTION
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  data-testid="decision-answer-input"
                  type="number"
                  step="0.1"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !feedback) submit();
                  }}
                  placeholder="Type the calculated value…"
                  autoFocus
                  disabled={!!feedback?.ok}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 10,
                    color: "#fff",
                    fontSize: 18,
                    outline: "none",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 600,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#9aa4b2",
                    fontSize: 14,
                    fontFamily: "'JetBrains Mono', monospace",
                    paddingRight: 6,
                  }}
                >
                  {unit}
                </div>
                <button
                  data-testid="decision-submit-btn"
                  onClick={submit}
                  disabled={!!feedback?.ok}
                  style={{
                    padding: "12px 24px",
                    background:
                      "linear-gradient(135deg, #ffd23f 0%, #ff4d6d 100%)",
                    color: "#1a0a0a",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 800,
                    cursor: feedback?.ok ? "default" : "pointer",
                    opacity: feedback?.ok ? 0.7 : 1,
                    letterSpacing: 1,
                  }}
                >
                  Commit
                </button>
              </div>
            </div>

            {feedback && (
              <div
                data-testid="decision-feedback"
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: feedback.ok
                    ? "rgba(0,255,170,0.18)"
                    : "rgba(255,77,109,0.15)",
                  color: feedback.ok ? "#00ffaa" : "#ff8a9b",
                  fontSize: 14,
                  fontWeight: 600,
                  border: `1px solid ${feedback.ok ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.4)"}`,
                  boxShadow: feedback.ok ? "0 0 24px rgba(0,255,170,0.3)" : "none",
                  textAlign: "center",
                }}
              >
                {feedback.msg}
              </div>
            )}

            <div
              style={{
                marginTop: 12,
                fontSize: 11,
                color: "#5a6478",
                textAlign: "center",
              }}
            >
              Commit your prediction — the simulation will test it. A wrong
              prediction plays out its real consequence in slow motion.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
